import { Button, Tab, Tabs, Typography } from '@mui/material';
import { useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react';

import { SavedSignatureList } from './SavedSignatureList';
import { SignatureCanvas, type SignatureCanvasHandle } from './SignatureCanvas';
import {
  applyNameStyle,
  renderTypedSignature,
  type NameStyle,
  type SignatureFont,
} from './typedSignature';
import { TypedSignatureFields } from './TypedSignatureFields';
import { useCurrentUser } from './useCurrentUser';

export interface SavedSignature {
  id: string;
  base64Image: string;
}

export interface ResolvedSignature {
  /** The chosen signature as a data URL. */
  image: string;
  /**
   * True when the director drew or typed this signature rather than picking one
   * they had already saved. Only a new one is worth offering to save, which is
   * what the sign dialogs use it for — re-saving a saved signature would grow
   * the list by a duplicate every time they signed.
   */
  isNew: boolean;
}

export interface SignaturePickerHandle {
  /** The chosen signature, or null when nothing usable is ready (nothing drawn,
   *  no name typed, or a drawing too detailed to compress). */
  resolve: () => ResolvedSignature | null;
}

type Mode = 'saved' | 'draw' | 'type';

/** `draw` is absent deliberately: the canvas carries its own instruction above
 *  it and an empty dashed box already reads as "nothing here yet", so a third
 *  line saying the same thing was only noise. */
const BLOCKED_REASON: Partial<Record<Mode, string>> = {
  saved: 'Choose a signature to continue.',
  type: 'Enter a name to continue.',
};

interface SignaturePickerProps {
  savedSignatures: readonly SavedSignature[];
  isLoadingSaved?: boolean;
  ref: Ref<SignaturePickerHandle>;
  /** Lets the parent enable or disable its submit button as the choice changes. */
  onReadyChange?: (isReady: boolean) => void;
  /** Whether the current choice is a signature the director has not saved yet.
   *  The sign dialogs offer to save only those. */
  onIsNewChange?: (isNew: boolean) => void;
}

/**
 * The active tab and selection, preferring what the director chose and falling
 * back to what the loaded list implies.
 *
 * Kept out of the component because the fallbacks are the subtle part: the
 * saved list arrives one render AFTER mount, so seeding `useState` from it
 * froze the empty first render — the tab stayed on Draw and nothing was
 * selected, leaving a director who already had a saved signature with a
 * disabled submit button until they picked one by hand.
 *
 * `isLoadingSaved` is what stops the fix becoming its own bug. Falling back to
 * `draw` while the list is still in flight shows the canvas, and the tab would
 * then jump to `saved` the moment the request lands — wiping a stroke a fast
 * director had already begun. Staying on `saved` until the answer is known
 * means the tab only ever settles once.
 */
const resolveDefaults = (
  savedSignatures: readonly SavedSignature[],
  isLoadingSaved: boolean,
  chosenMode: Mode | undefined,
  chosenId: string | undefined,
): { mode: Mode; selectedId: string } => ({
  mode: chosenMode ?? (isLoadingSaved || savedSignatures.length > 0 ? 'saved' : 'draw'),
  selectedId: chosenId ?? savedSignatures[0]?.id ?? '',
});

/**
 * Picks a signature to apply: a saved one, a freshly drawn one, or a typed one.
 *
 * All three methods live here because signing is where a director actually
 * needs them. Offering only saved signatures — as this app did until now — left
 * a first-time director at a dead end reading "add one in Settings, then come
 * back", which is a detour out of the task they came to do. The legacy portal
 * offered all three at sign time and was right to.
 *
 * Nothing is duplicated to achieve it: the canvas and the typed-name fields are
 * the same components Settings uses. The legacy portal had three copies of its
 * signature canvas, which is how they drifted apart.
 */
export const SignaturePicker = ({
  savedSignatures,
  isLoadingSaved = false,
  ref,
  onReadyChange,
  onIsNewChange,
}: SignaturePickerProps) => {
  const { fullName } = useCurrentUser();

  const hasSaved = savedSignatures.length > 0;

  // `undefined` means "not chosen yet" — see `resolveDefaults`.
  const [chosenMode, setChosenMode] = useState<Mode>();
  const [chosenId, setChosenId] = useState<string>();

  const { mode, selectedId } = resolveDefaults(
    savedSignatures,
    isLoadingSaved,
    chosenMode,
    chosenId,
  );

  const canvasRef = useRef<SignatureCanvasHandle>(null);
  const [hasDrawn, setHasDrawn] = useState(false);

  const [nameStyle, setNameStyle] = useState<NameStyle>('fullName');
  const [font, setFont] = useState<SignatureFont>('greatVibes');
  const [typedName, setTypedName] = useState('');

  const effectiveName = typedName.trim() === '' ? fullName : typedName;
  const previewText = applyNameStyle(effectiveName, nameStyle);

  const selected = savedSignatures.find((signature) => signature.id === selectedId);

  /** Whether the current mode has something usable to submit. One expression
   *  per mode, asked in one place — every caller now reads the reported value
   *  instead of re-deriving it. */
  const READY_IN: Record<Mode, boolean> = {
    saved: selected !== undefined,
    draw: hasDrawn,
    type: previewText !== '',
  };

  const isReady = READY_IN[mode];
  const blockedReason = BLOCKED_REASON[mode];

  // One mechanism, not two. Readiness was previously pushed by hand from each
  // event handler, which could not cover the transition that matters most here:
  // the saved list arrives after mount and supplies a default selection, so
  // readiness changes without the director touching anything. Deriving it and
  // reporting the changes covers every path, including that one.
  //
  // Guarded by the last reported value rather than fired on every render, so
  // the parent hears about genuine changes only.
  const lastReported = useRef<boolean>(undefined);
  useEffect(() => {
    if (lastReported.current === isReady) return;
    lastReported.current = isReady;
    onReadyChange?.(isReady);
  }, [isReady, onReadyChange]);

  // Reported the same way and for the same reason: the tab can settle on
  // `saved` once the list lands without the director touching anything, so the
  // parent cannot derive this from its own events either.
  const isNew = mode !== 'saved';
  const lastReportedIsNew = useRef<boolean>(undefined);
  useEffect(() => {
    if (lastReportedIsNew.current === isNew) return;
    lastReportedIsNew.current = isNew;
    onIsNewChange?.(isNew);
  }, [isNew, onIsNewChange]);

  useImperativeHandle(ref, () => ({
    resolve: () => {
      if (mode === 'saved') {
        const image = selected?.base64Image;
        return image === undefined ? null : { image, isNew: false };
      }

      const image =
        mode === 'draw'
          ? canvasRef.current?.toDataUrl()
          : previewText === ''
            ? null
            : renderTypedSignature(previewText, font);

      return image === null || image === undefined ? null : { image, isNew: true };
    },
  }));

  return (
    <div className="flex flex-col gap-4">
      <Tabs
        value={mode}
        onChange={(_event, next: Mode) => {
          setChosenMode(next);
        }}
        variant="fullWidth"
        aria-label="How to sign"
      >
        {/* Rendered but disabled rather than hidden when there is nothing
            saved: a tab that appears only sometimes makes the dialog look
            different to two directors describing it to each other. */}
        <Tab value="saved" label="Saved" disabled={!hasSaved && !isLoadingSaved} />
        <Tab value="draw" label="Draw" />
        <Tab value="type" label="Type" />
      </Tabs>

      {mode === 'saved' && (
        <SavedSignatureList
          signatures={savedSignatures}
          selectedId={selectedId}
          isLoading={isLoadingSaved}
          onSelect={setChosenId}
        />
      )}

      {mode === 'draw' && (
        <div className="flex flex-col gap-2">
          <Typography variant="body2" color="text.secondary">
            Sign in the box below using your finger or a stylus.
          </Typography>

          <SignatureCanvas
            ref={canvasRef}
            onDrawnChange={setHasDrawn}
            ariaLabel="Signature drawing area"
          />

          {/* A drawn signature is rarely right first time, and without this the
              only way back is to close the dialog and lose the rest of the
              flow. `clear` notifies through onDrawnChange, so `hasDrawn` and
              the parent's submit button both follow from it. */}
          <div className="flex justify-end">
            <Button
              onClick={() => {
                canvasRef.current?.clear();
              }}
              disabled={!hasDrawn}
              size="small"
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      {mode === 'type' && (
        <TypedSignatureFields
          fullName={fullName}
          typedName={typedName}
          onTypedNameChange={setTypedName}
          nameStyle={nameStyle}
          onNameStyleChange={setNameStyle}
          font={font}
          onFontChange={setFont}
          previewText={previewText}
        />
      )}

      {/* Why the parent's submit button is disabled, said in words. A control
          that greys out with no explanation leaves the director guessing, and
          on a phone the canvas may be below the fold. */}
      {!isReady && blockedReason !== undefined && (
        <Typography variant="caption" color="text.secondary" role="status">
          {blockedReason}
        </Typography>
      )}
    </div>
  );
};
